// FormationEvaluation.jsx - Gestion unifiée des formations et évaluations publiques
/*
Lien 1 (mode=postuler) : 
  Objectif : Inscription discrete ciblée via invitation. 
  Contraintes : la formation ou l evaluation n a pas besoin d etre public 
Lien 2 (mode=demander) :
  Objectif : Demander une formation/évaluation (publique) sans invitation.
  Contraintes : aucune 
*/
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { APP_CONFIG } from '../../config/appConfig';

import {
  Globe, Plus, Trash2, Search, Loader2,
  AlertCircle, CheckCircle, XCircle, Mail, Users,
  Copy, Link, Send, BookOpen, FileQuestion, Edit, Calendar
} from 'lucide-react';

export default function FormationEvaluation() {
  const { token, user, isAuthenticated } = useAuth();
  
  // eslint-disable-next-line no-unused-vars
  const userId = user?.id || 0;
  const [client, setClient] = useState(null);

  // États pour les données
  const [formations, setFormations] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [evaluationsRich, setEvaluationsRich] = useState([]); // ⭐ Nouvel état

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal d'invitation
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedFormations, setSelectedFormations] = useState([]);
  const [selectedEvaluations, setSelectedEvaluations] = useState([]);
  const [selectedEvaluationsRich, setSelectedEvaluationsRich] = useState([]); // ⭐
  const [inviteData, setInviteData] = useState({
    emails: '',
    sujet: '',
    messagePersonnalise: ''
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  // Modal de partage
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareItem, setShareItem] = useState(null);

  // Modal d'édition des dates
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editType, setEditType] = useState('FORMATION');
  const [editDates, setEditDates] = useState({ dateDebut: '', dateFin: '' });
  const [editLoading, setEditLoading] = useState(false);

  // Chargement initial
  const loadData = async () => {
    if (!token || !user?.id) return;
    setLoading(true);
    setError(null);
    try {
      // 1. Formations
      const formationsRes = await fetch(
        `${APP_CONFIG.BASE_URL}/api/v1/client/formations`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } }
      );
      let formationsData = [];
      if (formationsRes.ok) {
        const data = await formationsRes.json();
        formationsData = data.content || data.data || data || [];
      } else {
        throw new Error('Erreur lors du chargement des formations');
      }

      // 2. Évaluations classiques indépendantes
      const evaluationsRes = await fetch(
        `${APP_CONFIG.BASE_URL}/api/v1/client/evaluations`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } }
      );
      let evaluationsData = [];
      if (evaluationsRes.ok) {
        const data = await evaluationsRes.json();
        evaluationsData = (data.content || data.data || data || [])
          .filter(e => e.independante === true);
      } else {
        console.warn('Impossible de charger les évaluations');
      }

      // 3. ⭐ Évaluations enrichies publiques (on filtre côté frontend avec publique=true)
      const evaluationsRichRes = await fetch(
        `${APP_CONFIG.BASE_URL}/api/v1/client/rich/evaluations?publique=true&page=0&size=100`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } }
      );
      let evaluationsRichData = [];
      if (evaluationsRichRes.ok) {
        const data = await evaluationsRichRes.json();
        const items = data.content || data.data || data || [];
        evaluationsRichData = items.filter(e => e.publique === true);
      } else {
        console.warn('Impossible de charger les évaluations enrichies');
      }

      setFormations(formationsData);
      setEvaluations(evaluationsData);
      setEvaluationsRich(evaluationsRichData);

      // Charger le client
      const resClient = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/client/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resClient.ok) {
        const clientData = await resClient.json();
        setClient(clientData);
      } else {
        console.warn('Impossible de charger le client');
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Basculer le statut public (pour formations et évaluations classiques)
  const togglePublic = async (item, type) => {
    const id = item.id;
    const current = item.publique === true;
    const endpoint = type === 'FORMATION'
      ? `${APP_CONFIG.BASE_URL}/api/v1/client/formations/${id}/publique?publique=${!current}`
      : type === 'EVALUATION'
      ? `${APP_CONFIG.BASE_URL}/api/v1/client/evaluations/${id}/publique?publique=${!current}`
      : `${APP_CONFIG.BASE_URL}/api/v1/client/rich/evaluations/${id}/publique?publique=${!current}`; // si endpoint existe

    try {
      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const updated = await response.json();
        if (type === 'FORMATION') {
          setFormations(prev => prev.map(f => f.id === id ? updated : f));
        } else if (type === 'EVALUATION') {
          setEvaluations(prev => prev.map(e => e.id === id ? updated : e));
        } else {
          setEvaluationsRich(prev => prev.map(e => e.id === id ? updated : e));
        }
        if (!current) {
          setShareItem({ type, data: updated });
          setShowShareModal(true);
        }
      } else {
        const errData = await response.json();
        setError(errData.message || 'Erreur lors de la mise à jour');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Générer le lien d'invitation
  const getInvitationLink = (item, type) => {
    if (!client?.code) return '#';
    const baseUrl = window.location.origin;
    let typeParam = type;
    if (type === 'EVALUATION_RICH') typeParam = 'EVALUATION_RICH';
    return `${baseUrl}/inscription?mode=postuler&code=${client.code}&type=${typeParam}&id=${item.id}`;
  };

  // Copier dans le presse-papier
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('✅ Lien copié dans le presse-papier !');
    }).catch(() => {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert('✅ Lien copié dans le presse-papier !');
    });
  };

  // Envoyer les invitations
  const handleSendInvitations = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    setInviteResult(null);

    const emails = inviteData.emails.split('\n').map(e => e.trim()).filter(e => e);
    if (emails.length === 0) {
      setInviteResult({ error: 'Veuillez saisir au moins un email' });
      setInviteLoading(false);
      return;
    }
    if (selectedFormations.length === 0 && selectedEvaluations.length === 0 && selectedEvaluationsRich.length === 0) {
      setInviteResult({ error: 'Veuillez sélectionner au moins une formation, une évaluation ou une évaluation enrichie' });
      setInviteLoading(false);
      return;
    }

    const liens = [];
    selectedFormations.forEach(id => {
      const formation = formations.find(f => f.id === id);
      if (formation) {
        liens.push({
          type: 'FORMATION',
          id: formation.id,
          titre: formation.titre,
          lien: getInvitationLink(formation, 'FORMATION')
        });
      }
    });
    selectedEvaluations.forEach(id => {
      const evaluation = evaluations.find(e => e.id === id);
      if (evaluation) {
        liens.push({
          type: 'EVALUATION',
          id: evaluation.id,
          titre: evaluation.titre,
          lien: getInvitationLink(evaluation, 'EVALUATION')
        });
      }
    });
    selectedEvaluationsRich.forEach(id => {
      const evaluation = evaluationsRich.find(e => e.id === id);
      if (evaluation) {
        liens.push({
          type: 'EVALUATION_RICH',
          id: evaluation.id,
          titre: evaluation.titre,
          lien: getInvitationLink(evaluation, 'EVALUATION_RICH')
        });
      }
    });

    const clientId = client?.id || 0;
    const clientCode = client?.code || "";
    try {
      const response = await fetch(
        `${APP_CONFIG.BASE_URL}/api/v1/client/invitations/envoyer`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            clientId,
            clientCode,
            formationIds: selectedFormations,
            evaluationIds: selectedEvaluations,
            evaluationRichIds: selectedEvaluationsRich, // ⭐ Ajout
            emails,
            sujet: inviteData.sujet || 'Invitation à vous inscrire à nos formations et évaluations',
            messagePersonnalise: inviteData.messagePersonnalise,
            liens
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setInviteResult({ success: data });
        setInviteData({ emails: '', sujet: '', messagePersonnalise: '' });
        setSelectedFormations([]);
        setSelectedEvaluations([]);
        setSelectedEvaluationsRich([]);
      } else {
        const data = await response.json();
        setInviteResult({ error: data.message || 'Erreur lors de l\'envoi' });
      }
    } catch (err) {
      setInviteResult({ error: err.message });
    } finally {
      setInviteLoading(false);
    }
  };

  // Gestion des dates (identique)
  const openEditModal = (item, type) => {
    setEditItem(item);
    setEditType(type);
    setEditDates({
      dateDebut: item.dateDebutInscription ? item.dateDebutInscription.split('T')[0] : '',
      dateFin: item.dateFinInscription ? item.dateFinInscription.split('T')[0] : ''
    });
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    if (!editItem) return;
    setEditLoading(true);
    try {
      const endpoint = editType === 'FORMATION'
        ? `${APP_CONFIG.BASE_URL}/api/v1/client/formations/${editItem.id}`
        : editType === 'EVALUATION'
        ? `${APP_CONFIG.BASE_URL}/api/v1/client/evaluations/${editItem.id}`
        : `${APP_CONFIG.BASE_URL}/api/v1/client/rich/evaluations/${editItem.id}`;
      const payload = {
        ...editItem,
        dateDebutInscription: editDates.dateDebut || null,
        dateFinInscription: editDates.dateFin || null
      };
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Erreur lors de la mise à jour');
      }
      await loadData();
      setEditModalOpen(false);
      alert('✅ Dates mises à jour avec succès');
    } catch (err) {
      setError(err.message);
    } finally {
      setEditLoading(false);
    }
  };

  // Filtrage
  const filteredFormations = formations.filter(f =>
    f.titre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredEvaluations = evaluations.filter(e =>
    e.titre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredEvaluationsRich = evaluationsRich.filter(e =>
    e.titre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Affichage d'une carte (générique)
  const renderItem = (item, type) => {
    const isPublic = item.publique === true;
    const icon = type === 'FORMATION' ? <BookOpen size={16} /> : <FileQuestion size={16} />;
    const typeLabel = type === 'FORMATION' ? 'Formation' : type === 'EVALUATION_RICH' ? 'Évaluation enrichie' : 'Évaluation';

    const formatDate = (dateStr) => {
      if (!dateStr) return null;
      const d = new Date(dateStr);
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };
    const dateDebut = formatDate(item.dateDebutInscription);
    const dateFin = formatDate(item.dateFinInscription);

    const borderColor = type === 'FORMATION' ? 'border-l-blue-400' : type === 'EVALUATION_RICH' ? 'border-l-purple-600' : 'border-l-purple-400';

    return (
      <div key={item.id} className={`bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition ${borderColor}`}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              {icon}
              <h3 className="font-semibold text-gray-800">{item.titre}</h3>
            </div>
            <p className="text-sm text-gray-500 line-clamp-2">{item.description || 'Aucune description'}</p>
          </div>
          <button
            onClick={() => togglePublic(item, type)}
            className={`p-2 rounded-lg transition ${isPublic ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
            title={isPublic ? 'Visible publiquement' : 'Rendre public'}
          >
            {isPublic ? <CheckCircle size={20} /> : <XCircle size={20} />}
          </button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <span className={`px-2 py-1 rounded-full ${isPublic ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
            {isPublic ? '✅ Public' : '🔒 Privé'}
          </span>
          {type === 'FORMATION' && (
            <span className="px-2 py-1 bg-gray-100 rounded-full">{item.modules?.length || 0} modules</span>
          )}
          {item.code && <span className="px-2 py-1 bg-gray-100 rounded-full font-mono text-xs">{item.code}</span>}
          <span className="px-2 py-1 bg-gray-100 rounded-full">{typeLabel}</span>
        </div>

        {(dateDebut || dateFin) && (
          <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
            <Calendar size={12} className="shrink-0" />
            {dateDebut && dateFin && <span>Du {dateDebut} au {dateFin}</span>}
            {dateDebut && !dateFin && <span>À partir du {dateDebut}</span>}
            {!dateDebut && dateFin && <span>Jusqu'au {dateFin}</span>}
          </div>
        )}

        <div className="mt-3 pt-3 border-t border-gray-200 flex gap-2">
          <button
            onClick={() => { setShareItem({ type, data: item }); setShowShareModal(true); }}
            className="flex-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition text-xs font-semibold flex items-center justify-center gap-1"
          >
            <Link size={14} /> Partager
          </button>
          <button
            onClick={() => {
              if (type === 'FORMATION') setSelectedFormations([item.id]);
              else if (type === 'EVALUATION_RICH') setSelectedEvaluationsRich([item.id]);
              else setSelectedEvaluations([item.id]);
              setSelectedEvaluations([]);
              setSelectedFormations([]);
              setSelectedEvaluationsRich([]);
              setInviteResult(null);
              setShowInviteModal(true);
            }}
            className="flex-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition text-xs font-semibold flex items-center justify-center gap-1"
          >
            <Send size={14} /> Inviter
          </button>
          <button
            onClick={() => openEditModal(item, type)}
            className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition text-xs font-semibold flex items-center justify-center gap-1"
            title="Modifier les dates d'inscription"
          >
            <Edit size={14} />Delais
          </button>
        </div>
      </div>
    );
  };

  // Sécurité
  if (!isAuthenticated || !token) {
    return (
      <div className="p-6 text-center">
        <AlertCircle size={48} className="mx-auto text-amber-500 mb-4" />
        <p className="text-gray-600">Veuillez vous connecter</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Globe size={24} />
            Formations & Évaluations publiques
          </h2>
          <p className="text-sm text-gray-500">Gérez les contenus accessibles au public et envoyez des invitations</p>
          <div className="text-xs text-gray-400 mt-1">Code client : <span className="font-mono font-bold">{client?.code}</span></div>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-48 md:w-64"
          />
          <button
            onClick={() => {
              setSelectedFormations([]);
              setSelectedEvaluations([]);
              setSelectedEvaluationsRich([]);
              setInviteResult(null);
              setShowInviteModal(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-50"
            disabled={formations.length === 0 && evaluations.length === 0 && evaluationsRich.length === 0}
          >
            <Send size={16} />
            Inviter
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 flex items-center gap-2">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-blue-600" size={32} />
        </div>
      ) : (
        <>
          {/* Section Formations */}
          <section className="rounded-2xl border border-blue-100 bg-blue-50/40 p-4 md:p-5">
            <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-700">
                <BookOpen size={18} />
              </span>
              Mes Formations
              <span className="text-sm font-normal text-blue-400 ml-1">({filteredFormations.length})</span>
            </h3>
            {filteredFormations.length === 0 ? (
              <div className="text-center py-8 text-gray-400 border border-dashed border-blue-200 rounded-xl bg-white/60">
                <BookOpen size={32} className="mx-auto mb-2" />
                <p>Aucune formation disponible</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredFormations.map(f => renderItem(f, 'FORMATION'))}
              </div>
            )}
          </section>

          {/* Séparateur */}
          <div className="flex items-center gap-3 py-2">
            <div className="flex-1 border-t border-dashed border-gray-300" />
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Évaluations indépendantes</span>
            <div className="flex-1 border-t border-dashed border-gray-300" />
          </div>

          {/* Section Évaluations classiques */}
          <section className="rounded-2xl border border-purple-100 bg-purple-50/40 p-4 md:p-5">
            <h3 className="text-lg font-semibold text-purple-900 mb-3 flex items-center gap-2">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-purple-100 text-purple-700">
                <FileQuestion size={18} />
              </span>
              Mes Évaluations indépendantes
              <span className="text-sm font-normal text-purple-400 ml-1">({filteredEvaluations.length})</span>
            </h3>
            {filteredEvaluations.length === 0 ? (
              <div className="text-center py-8 text-gray-400 border border-dashed border-purple-200 rounded-xl bg-white/60">
                <FileQuestion size={32} className="mx-auto mb-2" />
                <p>Aucune évaluation indépendante disponible</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredEvaluations.map(e => renderItem(e, 'EVALUATION'))}
              </div>
            )}
          </section>

          {/* ⭐ Nouvelle section : Évaluations enrichies */}
          <div className="flex items-center gap-3 py-2">
            <div className="flex-1 border-t border-dashed border-purple-400" />
            <span className="text-xs font-semibold uppercase tracking-wide text-purple-600">Évaluations enrichies publiques</span>
            <div className="flex-1 border-t border-dashed border-purple-400" />
          </div>

          <section className="rounded-2xl border border-purple-300 bg-purple-50/40 p-4 md:p-5">
            <h3 className="text-lg font-semibold text-purple-900 mb-3 flex items-center gap-2">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-purple-200 text-purple-700">
                <FileQuestion size={18} />
              </span>
              Mes Évaluations enrichies publiques
              <span className="text-sm font-normal text-purple-400 ml-1">({filteredEvaluationsRich.length})</span>
            </h3>
            {filteredEvaluationsRich.length === 0 ? (
              <div className="text-center py-8 text-gray-400 border border-dashed border-purple-300 rounded-xl bg-white/60">
                <FileQuestion size={32} className="mx-auto mb-2 text-purple-300" />
                <p>Aucune évaluation enrichie publique disponible</p>
                <p className="text-xs text-gray-400">Rendez une évaluation enrichie publique pour qu'elle apparaisse ici.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredEvaluationsRich.map(e => renderItem(e, 'EVALUATION_RICH'))}
              </div>
            )}
          </section>
        </>
      )}

      {/* Modals (inchangés sauf l'ajout des évaluations enrichies dans le modal d'invitation) */}
      {showShareModal && shareItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold flex items-center gap-2">
                <Link size={20} className="text-blue-600" />
                Partager {shareItem.type === 'FORMATION' ? 'la formation' : shareItem.type === 'EVALUATION_RICH' ? "l'évaluation enrichie" : "l'évaluation"}
              </h3>
              <button onClick={() => { setShowShareModal(false); setShareItem(null); }} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-700">{shareItem.data.titre}</p>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${shareItem.data.publique ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                    {shareItem.data.publique ? 'Public' : 'Privé'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{shareItem.data.description}</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Lien d'invitation</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={getInvitationLink(shareItem.data, shareItem.type)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono truncate"
                  />
                  <button
                    onClick={() => copyToClipboard(getInvitationLink(shareItem.data, shareItem.type))}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                  >
                    <Copy size={16} /> Copier
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Ce lien permet à un étudiant de postuler directement.</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                💡 Vous pouvez envoyer ce lien par email, SMS ou le partager sur les réseaux sociaux.
                {!shareItem.data.publique && (
                  <> Cet élément n'est pas listé publiquement : seules les personnes ayant ce lien pourront postuler.</>
                )}
              </div>
              <button
                onClick={() => {
                  setShowShareModal(false);
                  if (shareItem.type === 'FORMATION') {
                    setSelectedFormations([shareItem.data.id]);
                    setSelectedEvaluations([]);
                    setSelectedEvaluationsRich([]);
                  } else if (shareItem.type === 'EVALUATION_RICH') {
                    setSelectedEvaluationsRich([shareItem.data.id]);
                    setSelectedFormations([]);
                    setSelectedEvaluations([]);
                  } else {
                    setSelectedEvaluations([shareItem.data.id]);
                    setSelectedFormations([]);
                    setSelectedEvaluationsRich([]);
                  }
                  setInviteResult(null);
                  setShowInviteModal(true);
                }}
                className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-2"
              >
                <Send size={16} /> Envoyer par email
              </button>
            </div>
          </div>
        </div>
      )}

      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold flex items-center gap-2">
                <Mail size={20} className="text-blue-600" />
                Envoyer des invitations
              </h3>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteResult(null);
                  setSelectedFormations([]);
                  setSelectedEvaluations([]);
                  setSelectedEvaluationsRich([]);
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSendInvitations} className="space-y-4">
              {/* Formations */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Formations</label>
                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {formations.length > 0 ? (
                    formations.map(f => (
                      <label key={f.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={selectedFormations.includes(f.id)}
                          onChange={() => {
                            setSelectedFormations(prev =>
                              prev.includes(f.id) ? prev.filter(id => id !== f.id) : [...prev, f.id]
                            );
                          }}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm">{f.titre}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${f.publique ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {f.publique ? 'Public' : 'Privé'}
                        </span>
                        <span className="text-xs text-gray-400 ml-auto truncate max-w-xs">
                          {getInvitationLink(f, 'FORMATION')}
                        </span>
                      </label>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-2">Aucune formation disponible.</p>
                  )}
                </div>
              </div>

              {/* Évaluations classiques */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Évaluations</label>
                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {evaluations.length > 0 ? (
                    evaluations.map(e => (
                      <label key={e.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={selectedEvaluations.includes(e.id)}
                          onChange={() => {
                            setSelectedEvaluations(prev =>
                              prev.includes(e.id) ? prev.filter(id => id !== e.id) : [...prev, e.id]
                            );
                          }}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm">{e.titre}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${e.publique ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {e.publique ? 'Public' : 'Privé'}
                        </span>
                        <span className="text-xs text-gray-400 ml-auto truncate max-w-xs">
                          {getInvitationLink(e, 'EVALUATION')}
                        </span>
                      </label>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-2">Aucune évaluation disponible.</p>
                  )}
                </div>
              </div>

              {/* ⭐ Évaluations enrichies */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Évaluations enrichies</label>
                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {evaluationsRich.length > 0 ? (
                    evaluationsRich.map(e => (
                      <label key={e.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={selectedEvaluationsRich.includes(e.id)}
                          onChange={() => {
                            setSelectedEvaluationsRich(prev =>
                              prev.includes(e.id) ? prev.filter(id => id !== e.id) : [...prev, e.id]
                            );
                          }}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm">{e.titre}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${e.publique ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {e.publique ? 'Public' : 'Privé'}
                        </span>
                        <span className="text-xs text-gray-400 ml-auto truncate max-w-xs">
                          {getInvitationLink(e, 'EVALUATION_RICH')}
                        </span>
                      </label>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-2">Aucune évaluation enrichie disponible.</p>
                  )}
                </div>
              </div>

              <div className="text-xs text-gray-400">
                {selectedFormations.length + selectedEvaluations.length + selectedEvaluationsRich.length} élément(s) sélectionné(s)
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Emails des étudiants *</label>
                <textarea
                  value={inviteData.emails}
                  onChange={(e) => setInviteData(prev => ({ ...prev, emails: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="jean@email.com&#10;marie@email.com"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Un email par ligne</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Sujet</label>
                <input
                  type="text"
                  value={inviteData.sujet}
                  onChange={(e) => setInviteData(prev => ({ ...prev, sujet: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Invitation à vous inscrire à nos formations et évaluations"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Message personnalisé</label>
                <textarea
                  value={inviteData.messagePersonnalise}
                  onChange={(e) => setInviteData(prev => ({ ...prev, messagePersonnalise: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Bonjour, nous avons le plaisir de vous inviter..."
                />
              </div>

              {inviteResult && (
                <div className={`p-4 rounded-lg ${inviteResult.error ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-green-50 border border-green-200 text-green-700'}`}>
                  {inviteResult.error ? (
                    <div className="flex items-center gap-2"><AlertCircle size={20} /> {inviteResult.error}</div>
                  ) : (
                    <div>
                      <div className="flex items-center gap-2"><CheckCircle size={20} /> <span className="font-semibold">Invitations envoyées !</span></div>
                      <p className="text-sm mt-1">{inviteResult.success.envoyes} envoyé(s), {inviteResult.success.erreurs} erreur(s)</p>
                      {inviteResult.success.liens && (
                        <div className="mt-2 text-xs">
                          <p className="font-semibold">Liens générés :</p>
                          {inviteResult.success.liens.map((l, i) => (
                            <div key={i} className="text-green-600 truncate">{l.email} → {l.lien}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteResult(null);
                    setSelectedFormations([]);
                    setSelectedEvaluations([]);
                    setSelectedEvaluationsRich([]);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={inviteLoading || (selectedFormations.length === 0 && selectedEvaluations.length === 0 && selectedEvaluationsRich.length === 0)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 text-sm flex items-center gap-2"
                >
                  {inviteLoading ? (
                    <><Loader2 className="animate-spin" size={16} /> Envoi...</>
                  ) : (
                    <><Send size={16} /> Envoyer</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'édition des dates (inchangé) */}
      {editModalOpen && editItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold flex items-center gap-2">
                <Calendar size={20} className="text-blue-600" />
                Modifier les dates - {editItem.titre}
              </h3>
              <button onClick={() => setEditModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de début d'inscription</label>
                <input
                  type="date"
                  value={editDates.dateDebut}
                  onChange={(e) => setEditDates(prev => ({ ...prev, dateDebut: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin d'inscription</label>
                <input
                  type="date"
                  value={editDates.dateFin}
                  onChange={(e) => setEditDates(prev => ({ ...prev, dateFin: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={() => setEditModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm">Annuler</button>
                <button onClick={handleEditSave} disabled={editLoading} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 text-sm">
                  {editLoading ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}